import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { useContractServices } from '../../context/ContractServicesContext';

/**
 * A modal component for redeeming/selling Innovation Units
 */
const RedeemIUModal = ({ 
  project, 
  isOpen, 
  onClose, 
  getExplorerUrl,
  onSuccess
}) => {
  // Contract services
  const { services, executeContractTransaction, isReady } = useContractServices();

  // Form state
  const [redeemUnits, setRedeemUnits] = useState("");
  const [userIUBalance, setUserIUBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  // Fee and redemption return breakdown state
  const [sellFeePercentage, setSellFeePercentage] = useState({ raw: 0, formatted: '0.00' });
  const [redemptionBreakdown, setRedemptionBreakdown] = useState({
    baseReturn: ethers.constants.Zero,
    fee: ethers.constants.Zero,
    netReturn: ethers.constants.Zero,
    formattedBase: '0',
    formattedFee: '0',
    formattedNet: '0'
  });

  // Fetch user's IU balance and sell fee when modal opens
  useEffect(() => {
    if (isOpen && isReady && project) {
      fetchUserIUBalance();
      fetchSellFeePercentage();
    }
  }, [isOpen, isReady, project]);

  // Recalculate redemption breakdown when redeem units changes
  useEffect(() => {
    if (redeemUnits && Number(redeemUnits) > 0 && isReady && project) {
      calculateRedemptionBreakdown(redeemUnits);
    } else {
      setRedemptionBreakdown({
        baseReturn: ethers.constants.Zero,
        fee: ethers.constants.Zero,
        netReturn: ethers.constants.Zero,
        formattedBase: '0',
        formattedFee: '0',
        formattedNet: '0'
      });
    }
  }, [redeemUnits, isReady, project]);

  const fetchUserIUBalance = async () => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      setLoadingBalance(true);
      
      // Get project ID
      const projectId = getProjectId();
      
      // Get signer's address
      const address = await services.innovationUnits.contract.signer.getAddress();
      
      // Get IU balance
      const balance = await services.innovationUnits.balanceOf(address, projectId);
      setUserIUBalance(balance.toString());
    } catch (error) {
      console.error("Error fetching IU balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Function to fetch sell fee percentage
  const fetchSellFeePercentage = async () => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      // Get the sell fee percentage from the contract
      const sellFeeInBasisPoints = await services.innovationUnits.contract.sellFeePercentage();
      // Convert basis points (1/100 of a percent) to percentage
      // 100 basis points = 1%
      const formattedPercentage = sellFeeInBasisPoints.toNumber() / 100;
      
      setSellFeePercentage({
        raw: sellFeeInBasisPoints,
        formatted: formattedPercentage.toFixed(2)
      });
    } catch (error) {
      console.error("Error fetching sell fee percentage:", error);
      setSellFeePercentage({
        raw: 0,
        formatted: '0.00'
      });
    }
  };

  // Calculate the redemption breakdown using the contract function
  const calculateRedemptionBreakdown = async (units) => {
    if (!isReady || !services?.innovationUnits || !units || Number(units) <= 0) {
      setRedemptionBreakdown({
        baseReturn: ethers.constants.Zero,
        fee: ethers.constants.Zero,
        netReturn: ethers.constants.Zero,
        formattedBase: '0',
        formattedFee: '0',
        formattedNet: '0'
      });
      return;
    }
    
    try {
      const projectId = getProjectId();
      const iuAmount = ethers.BigNumber.from(Math.floor(parseFloat(units)));
      
      // Call the contract method to calculate selling return
      const { baseReturn, fee, netReturn } = await services.innovationUnits.calculateSellingReturn(
        projectId, 
        iuAmount
      );
      
      setRedemptionBreakdown({
        baseReturn,
        fee,
        netReturn,
        formattedBase: ethers.utils.formatEther(baseReturn),
        formattedFee: ethers.utils.formatEther(fee),
        formattedNet: ethers.utils.formatEther(netReturn)
      });
      
      return { baseReturn, fee, netReturn };
    } catch (error) {
      console.error("Error calculating selling return:", error);
      setRedemptionBreakdown({
        baseReturn: ethers.constants.Zero,
        fee: ethers.constants.Zero,
        netReturn: ethers.constants.Zero,
        formattedBase: '0',
        formattedFee: '0',
        formattedNet: '0'
      });
      return null;
    }
  };

  // Get the projectId from the project object
  const getProjectId = () => {
    if (project?.transactionData?.projectId) {
      return parseInt(project.transactionData.projectId);
    } else if (project?.projectId) {
      return parseInt(project.projectId);
    } else if (project?.projectTokenId) {
      return parseInt(project.projectTokenId);
    }
    
    throw new Error("Invalid project ID. Please ensure the project has a valid blockchain ID.");
  };

  // Handle redeem units input change
  const handleRedeemUnitsChange = async (e) => {
    const value = e.target.value;
    setRedeemUnits(value);
  };

  // Set max redeemable amount
  const setMaxAmount = () => {
    if (userIUBalance) {
      setRedeemUnits(userIUBalance);
    }
  };

  // Submit redeem transaction
  const submitRedeem = async () => {
    if (!isReady) {
      toast.error("Smart contract services are not ready. Please connect your wallet and try again.");
      return;
    }
    
    if (!redeemUnits || parseFloat(redeemUnits) <= 0) {
      toast.error("Please enter a valid amount of Innovation Units to redeem");
      return;
    }
    
    // Check if user has enough IUs
    if (userIUBalance && parseFloat(redeemUnits) > parseFloat(userIUBalance)) {
      toast.error(`Insufficient IU balance. You have ${parseFloat(userIUBalance)} IUs but trying to redeem ${parseFloat(redeemUnits)} IUs.`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Get the project ID
      const projectId = getProjectId();
      
      // Convert to wei amounts
      const iuAmount = ethers.BigNumber.from(Math.floor(parseFloat(redeemUnits)));
      
      // Calculate return to show user
      const breakdown = await calculateRedemptionBreakdown(redeemUnits);
      if (!breakdown) {
        throw new Error("Failed to calculate redemption amount");
      }
      
      // Check if the project treasury has enough CEL tokens to pay the user
      const treasuryBalance = await services.innovationUnits.getProjectTreasuryBalance(projectId);
      if (treasuryBalance.raw.lt(breakdown.baseReturn)) {
        throw new Error(`Insufficient project treasury balance. The project has ${treasuryBalance.formatted} CEL but needs ${ethers.utils.formatEther(breakdown.baseReturn)} CEL to complete this transaction.`);
      }
      
      // Show toast indicating the process has started
      toast.info(
        <div>
          <p className="font-bold">Redemption Process Started</p>
          <p className="text-sm mt-1">Please confirm the transaction in your wallet when prompted.</p>
        </div>,
        { autoClose: 5000, position: "top-center" }
      );
      
      // Execute the sell transaction
      const result = await executeContractTransaction(
        services.innovationUnits.sellIUs.bind(services.innovationUnits),
        [projectId, iuAmount],
        {
          pendingMessage: 'Processing redemption transaction...',
          successMessage: 'Redemption transaction submitted successfully!',
          failMessage: 'Failed to complete redemption',
          showToast: false // Don't show default toast, we'll handle it
        }
      );
      
      // Log the full transaction result for debugging
      console.log("Full redemption transaction result:", result);
      
      // If transaction was successful
      const txHash = result?.transactionHash || result?.hash;
      
      if (txHash) {
        console.log(`Redemption transaction successful with hash: ${txHash}`);
        
        // Toast success with details about the transaction
        toast.success(
          <div>
            <p className="font-bold">Redemption Successful!</p>
            <p className="text-sm mt-1">You have redeemed {redeemUnits} IUs for {redemptionBreakdown.formattedNet} CEL</p>
            <div className="text-xs mt-2">
              <a 
                href={getExplorerUrl(txHash)}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                View transaction
              </a>
            </div>
          </div>,
          { autoClose: 8000, position: "top-center" }
        );
        
        // Close the modal and reset fields
        onClose();
        setRedeemUnits("");
        
        // Call the success callback if provided
        if (onSuccess) {
          // Create redemption data object
          const redemptionData = {
            redeemUnits: redeemUnits,
            totalReturn: redemptionBreakdown.netReturn
          };
          
          onSuccess(txHash, redemptionData);
        }
      } else {
        console.error("Transaction result missing hash:", result);
        toast.error("Redemption transaction may have failed. Please check your wallet and refresh the page.");
      }
    } catch (error) {
      console.error("Redemption error:", error);
      
      // Check for specific error types for better user feedback
      let errorMessage = error.message || 'Failed to complete redemption';
      
      if (errorMessage.includes("user rejected") || errorMessage.includes("User denied")) {
        toast.error("Transaction was rejected. Please try again and approve the transaction in your wallet.");
      } else if (errorMessage.includes("Insufficient balance")) {
        toast.error("You don't have enough IUs to complete this transaction.");
      } else if (errorMessage.includes("Insufficient project treasury balance")) {
        toast.error("There are not enough CEL tokens in the project treasury to complete this redemption. Please try a smaller amount or contact the project owner.");
      } else {
        toast.error(`Redemption failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Redeem Innovation Units</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="mb-6">
          <div className="p-3 bg-blue-50 rounded text-sm mb-4">
            <h4 className="font-semibold mb-1">Redemption Information</h4>
            <p>Project: {project?.title}</p>
            <p>Price per IU: {project?.pricePerUnit} CEL</p>
            {sellFeePercentage?.formatted && (
              <p className="text-amber-600">Fee: {sellFeePercentage.formatted}%</p>
            )}
            {userIUBalance !== null ? (
              <p className="mt-1">Your IU balance: {parseFloat(userIUBalance).toFixed(2)} IUs</p>
            ) : loadingBalance ? (
              <p className="mt-1 text-gray-500">Loading your balance...</p>
            ) : null}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Number of Innovation Units to redeem
            </label>
            <div className="relative">
              <input
                type="number"
                value={redeemUnits}
                onChange={handleRedeemUnitsChange}
                placeholder="Enter amount of IUs"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
              {userIUBalance && (
                <button
                  type="button"
                  onClick={setMaxAmount}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-2 py-1 rounded"
                >
                  Max
                </button>
              )}
            </div>
            {redeemUnits && userIUBalance && parseFloat(redeemUnits) > parseFloat(userIUBalance) && (
              <p className="text-xs text-red-500 mt-1">
                Amount exceeds your balance of {parseFloat(userIUBalance).toFixed(2)} IUs
              </p>
            )}
          </div>
          
          {/* Redemption Breakdown */}
          {redeemUnits && Number(redeemUnits) > 0 && (
            <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="font-semibold mb-2 text-sm">Redemption Breakdown</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Return:</span>
                  <span>{parseFloat(redemptionBreakdown.formattedBase).toFixed(4)} CEL</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fee ({sellFeePercentage.formatted}%):</span>
                  <span className="text-amber-600">-{parseFloat(redemptionBreakdown.formattedFee).toFixed(4)} CEL</span>
                </div>
                <div className="border-t pt-2 mt-1">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Net Return:</span>
                    <span>{parseFloat(redemptionBreakdown.formattedNet).toFixed(4)} CEL</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Redemption Preview */}
          {redeemUnits && parseFloat(redeemUnits) > 0 && redemptionBreakdown.netReturn.gt(0) && (
            <div className="mb-6 p-3 bg-gray-50 rounded">
              <h4 className="font-semibold mb-2 text-sm">Redemption Preview</h4>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Units to redeem:</span>
                <span className="font-medium">{parseFloat(redeemUnits).toFixed(2)} IUs</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Expected return:</span>
                <span className="font-medium">{parseFloat(redemptionBreakdown.formattedNet).toFixed(4)} CEL</span>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Note: A {sellFeePercentage.formatted}% fee is applied when redeeming IUs.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={submitRedeem}
            disabled={
              loading || 
              !redeemUnits || 
              parseFloat(redeemUnits) <= 0 || 
              (userIUBalance && parseFloat(redeemUnits) > parseFloat(userIUBalance))
            }
            className="w-full px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Confirm Redemption'}
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedeemIUModal; 